export type UserRole =
  | 'SUPER_ADMIN'
  | 'SCHOOL_ADMIN'
  | 'TEACHER'
  | 'PARENT'
  | 'STUDENT'
  | 'DONOR'
  | 'PUBLIC';

/** Roles allowed to sign in to the TFM Admin portal. */
export const ADMIN_PORTAL_ROLES: readonly UserRole[] = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];

export function isAdminPortalRole(role: UserRole | null | undefined): boolean {
  return role === 'SUPER_ADMIN' || role === 'SCHOOL_ADMIN';
}

export type UserStatus = 'pending' | 'active' | 'suspended' | 'rejected';

export interface AuthUser {
  id: string;
  fullName: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  schoolId: string | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
}

export interface AuthResponse extends AuthTokens {
  user: AuthUser;
}

export interface ApiSuccess<T> {
  success: true;
  statusCode: number;
  message: string;
  data: T;
}

export interface ApiFailure {
  success: false;
  statusCode: number;
  message: string;
  errors?: Array<{ field: string; message: string }> | null;
}

export interface ManagedUser {
  id: string;
  fullName: string;
  email: string;
  username: string;
  role: UserRole;
  status: UserStatus;
  isActive: boolean;
  schoolId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedUsers {
  items: ManagedUser[];
  total: number;
  page: number;
  limit: number;
}

export type RoleChangeRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface RoleChangeRequest {
  id: string;
  requesterUserId: string;
  targetUserId: string;
  currentRole: UserRole;
  requestedRole: UserRole;
  schoolId: string | null;
  reason?: string;
  status: RoleChangeRequestStatus;
  reviewNote?: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityLog {
  id: string;
  actorUserId: string | null;
  action: string;
  resourceType?: string;
  resourceId?: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface PaginatedActivityLogs {
  items: ActivityLog[];
  total: number;
  page: number;
  limit: number;
}

export type LifecycleAction = 'activate' | 'deactivate' | 'suspend' | 'reject';

export interface RegisterTeacherInput {
  firstName: string;
  middleName?: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  phone?: string;
  age: number;
  gender: 'male' | 'female';
  cnic: string;
  schoolId: string;
}

export interface RegisterStudentInput {
  firstName: string;
  middleName?: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  phone?: string;
  age: number;
  gender: 'male' | 'female';
  cnic: string;
  schoolId: string;
}
